import { format, isValid, parse } from 'date-fns';

export type ParsedDateInput = {
  date: Date | null;
  viewMonth: Date | null;
};

const FULL_FORMATS = [
  'dd.MM.yyyy',
  'd.M.yyyy',
  'd.M.yy',
  'yyyy-MM-dd',
  'MM/dd/yyyy',
  'MM/dd/yy',
  'M/d/yyyy',
  'M/d/yy',
];

function tryParse(value: string, fmt: string, ref: Date): Date | null {
  const parsed = parse(value, fmt, ref);
  if (!isValid(parsed)) return null;
  if (format(parsed, fmt) !== value) return null;
  return parsed;
}

function buildDate(year: number, month0: number, day: number): Date | null {
  const d = new Date(year, month0, day);
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month0 ||
    d.getDate() !== day
  ) {
    return null;
  }
  return d;
}

export function parseDateInput(input: string, today: Date): ParsedDateInput {
  const value = input.trim();
  if (!value) return { date: null, viewMonth: null };

  for (const fmt of FULL_FORMATS) {
    const parsed = tryParse(value, fmt, today);
    if (parsed) return { date: parsed, viewMonth: parsed };
  }

  const dotPartial = /^(\d{1,2})\.(\d{1,2})\.?$/.exec(value);
  if (dotPartial) {
    const day = Number(dotPartial[1]);
    const month0 = Number(dotPartial[2]) - 1;
    const date = buildDate(today.getFullYear(), month0, day);
    if (date) return { date, viewMonth: date };
  }

  const dayOnly = /^(\d{1,2})\.?$/.exec(value);
  if (dayOnly) {
    const day = Number(dayOnly[1]);
    const date = buildDate(today.getFullYear(), today.getMonth(), day);
    if (date) return { date, viewMonth: date };
  }

  const usPartial = /^(\d{1,2})\/(\d{1,2})$/.exec(value);
  if (usPartial) {
    const month0 = Number(usPartial[1]) - 1;
    const day = Number(usPartial[2]);
    const date = buildDate(today.getFullYear(), month0, day);
    if (date) return { date, viewMonth: date };
  }

  const monthYear = /^(\d{1,2})\.(\d{4})$/.exec(value);
  if (monthYear) {
    const month0 = Number(monthYear[1]) - 1;
    const year = Number(monthYear[2]);
    if (month0 >= 0 && month0 <= 11) {
      const firstOfMonth = new Date(year, month0, 1);
      return { date: firstOfMonth, viewMonth: firstOfMonth };
    }
  }

  const yearOnly = /^(\d{4})$/.exec(value);
  if (yearOnly) {
    const year = Number(yearOnly[1]);
    const firstOfYear = new Date(year, 0, 1);
    return { date: firstOfYear, viewMonth: firstOfYear };
  }

  return { date: null, viewMonth: null };
}
